import { getThumbmark } from '@thumbmarkjs/thumbmarkjs'
import AxiosLib, { AxiosInstance, AxiosError } from 'axios'

import { isUnauthorizedUserErrorType, UnauthorizedUserErrorType } from 'types/UnauthorizedErrorType'
import { getDeviceIdCookie } from 'utils/cookie'
import { isServerError } from 'utils/isServerError'
import { rollbar } from 'utils/rollbar'

import { getConfiguration, backendApiUrl, isDevEnvironment } from './config'

type UnauthorizedUserHandler = (type: UnauthorizedUserErrorType) => void

const { countryCode } = getConfiguration()

const thumbmarkPromise = getThumbmark()

const baseURL = isDevEnvironment
  ? `${backendApiUrl}/${countryCode}`
  : `${window.origin}/ai-backend/${countryCode}`

let csrfToken = ''
let unauthorizedUserHandler: UnauthorizedUserHandler | null = null

export const axios: AxiosInstance = AxiosLib.create({
  baseURL,
  withCredentials: true,
})

axios.interceptors.request.use(
  async (config) => {
    config.headers['X-Requested-With'] = 'XMLHttpRequest'

    const isMutationRequest = config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())

    if (isMutationRequest) {
      config.headers['X-CSRF-Token'] = csrfToken
    }

    try {
      const { thumbmark } = await thumbmarkPromise
      config.headers['X-Device-Signature'] = thumbmark
      config.headers['X-Device-Id'] = getDeviceIdCookie()
    }
    catch {
      // Silently fail - don't block requests if thumbmark fails
    }

    return config
  },
)

axios.interceptors.response.use(
  (response) => {
    const newToken = response.headers['x-csrf-token']
    if (newToken) csrfToken = newToken
    return response
  },
  (error: AxiosError<{ type: UnauthorizedUserErrorType }>) => {
    const status = error.response?.status
    const type = error.response?.data?.type

    const isUnauthorizedError = status === 401 && isUnauthorizedUserErrorType(type)

    if (isUnauthorizedError) unauthorizedUserHandler?.(type)

    if (isServerError(status)) rollbar.error('API Request Failed', error)

    return Promise.reject(error)
  },
)

export const setUnauthorizedUserHandler = (handler: UnauthorizedUserHandler | null) => {
  unauthorizedUserHandler = handler
}

export const setBaseURL = (url: string) => {
  axios.defaults.baseURL = url
}
